import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, Container,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, alpha, Fade, Chip,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Face as FaceIcon,
  Public as PublicIcon,
  DashboardOutlined as DashboardIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import axios from '../../utils/axios';
import { formatMalaysiaNowString, formatMalaysiaTime } from '../../utils/malaysiaTime';
import Header from '../common/Header';
import Loading from '../common/Loading';
import { motion } from 'framer-motion';

// ─── Design tokens ────────────────────────────────────────────────────────────
const B700 = '#1D4ED8';
const B900 = '#1E3A8A';
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';
const CORAL = '#F87171';
const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';
const TEXT_BODY    = '#E2E8F0';
const TEXT_MUTED   = '#64748B';

const CHECKIN_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Orbitron:wght@700;900&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@400;500;600&display=swap');

  @keyframes bar-sweep  { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  @keyframes face-pulse {
    0%  { box-shadow: 0 0 0 0 rgba(56,189,248,0.55); }
    70% { box-shadow: 0 0 0 16px rgba(56,189,248,0); }
    100%{ box-shadow: 0 0 0 0 rgba(56,189,248,0); }
  }
  @keyframes hud-rotate     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes hud-rotate-rev { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
  @keyframes scan-line      { 0%{top:-4px} 100%{top:100%} }
  @keyframes servo-tick     { 0%,100%{transform:rotate(-2deg)} 50%{transform:rotate(2deg)} }
  @keyframes status-beat    { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.4);opacity:0.55} }
  @keyframes circuit-flow   { 0%{stroke-dashoffset:400} 100%{stroke-dashoffset:0} }
  @keyframes glitch-x       { 0%,100%{clip-path:inset(0 0 100% 0)} 15%{clip-path:inset(30% 0 55% 0)} 40%{clip-path:inset(65% 0 10% 0)} 70%{clip-path:inset(15% 0 75% 0)} }

  .robot-title {
    font-family: 'Orbitron', monospace;
    font-weight: 900;
    letter-spacing: 3px;
    text-transform: uppercase;
    position: relative;
  }
  .robot-title::after {
    content: attr(data-text);
    position: absolute; left: 2px; top: 0;
    color: #38BDF8; opacity: 0.13;
    animation: glitch-x 7s linear infinite;
  }
  .mono { font-family: 'Share Tech Mono', monospace; }
  .raj  { font-family: 'Rajdhani', sans-serif; }
  .dm   { font-family: 'DM Sans', sans-serif; }
`;

const MotionBox = motion(Box);

// ─── Geo ──────────────────────────────────────────────────────────────────────
function getFaceAttendanceCoordinates() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('GEO_UNSUPPORTED')); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy_m: typeof pos.coords.accuracy === 'number' ? pos.coords.accuracy : null,
      }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED)  reject(new Error('GEO_DENIED'));
        else if (err.code === err.TIMEOUT)        reject(new Error('GEO_TIMEOUT'));
        else                                      reject(new Error('GEO_UNAVAILABLE'));
      },
      { enableHighAccuracy: true, timeout: 25000, maximumAge: 0 }
    );
  });
}

// ─── HUD Ring ─────────────────────────────────────────────────────────────────
const HudRing = ({ size = 90, color = NEON, speed = '8s', reverse = false, dashes = '12 4' }) => (
  <Box sx={{ position: 'absolute', width: size, height: size, top: '50%', left: '50%',
    transform: 'translate(-50%,-50%)', pointerEvents: 'none', zIndex: 0 }}>
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{ position: 'absolute', animation: `${reverse ? 'hud-rotate-rev' : 'hud-rotate'} ${speed} linear infinite` }}>
      <circle cx={size/2} cy={size/2} r={size/2 - 2}
        fill="none" stroke={color} strokeWidth="1" strokeDasharray={dashes} strokeOpacity="0.5" />
    </svg>
  </Box>
);

// ─── Corner Bracket ───────────────────────────────────────────────────────────
const CornerBracket = ({ pos, color = NEON, size = 22, thick = 2 }) => {
  const borderMap = {
    tl: { borderTop: `${thick}px solid ${color}`, borderLeft: `${thick}px solid ${color}`, borderRadius: '3px 0 0 0' },
    tr: { borderTop: `${thick}px solid ${color}`, borderRight: `${thick}px solid ${color}`, borderRadius: '0 3px 0 0' },
    bl: { borderBottom: `${thick}px solid ${color}`, borderLeft: `${thick}px solid ${color}`, borderRadius: '0 0 0 3px' },
    br: { borderBottom: `${thick}px solid ${color}`, borderRight: `${thick}px solid ${color}`, borderRadius: '0 0 3px 0' },
  };
  return <Box sx={{ position: 'absolute', width: size, height: size, pointerEvents: 'none', ...pos, ...borderMap[pos.corner] }} />;
};

// ─── Circuit BG ───────────────────────────────────────────────────────────────
const CircuitBg = ({ opacity = 0.1 }) => (
  <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0, opacity }}>
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="cktbg" width="80" height="80" patternUnits="userSpaceOnUse">
          <path d="M0 40 H30 M30 40 L40 30 H60 M60 30 H80" stroke={NEON} strokeWidth="0.6" fill="none" strokeDasharray="60" style={{ animation: 'circuit-flow 3s linear infinite' }} />
          <path d="M40 0 V20 M40 20 L50 30" stroke={NEON} strokeWidth="0.6" fill="none" strokeDasharray="60" style={{ animation: 'circuit-flow 4s linear infinite reverse' }} />
          <path d="M0 60 H20 M20 60 L30 70 H50 M50 70 H80" stroke={NEON2} strokeWidth="0.5" fill="none" strokeDasharray="60" style={{ animation: 'circuit-flow 5s linear infinite' }} />
          <circle cx="30" cy="40" r="2" fill={NEON} fillOpacity="0.7" />
          <circle cx="60" cy="30" r="2" fill={NEON} fillOpacity="0.7" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#cktbg)" />
    </svg>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const PublicAttendanceCheckIn = () => {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const resultRef = useRef(null);

  const [loading,          setLoading]          = useState(false);
  const [cameraOpen,       setCameraOpen]       = useState(false);
  const [error,            setError]            = useState(null);
  const [success,          setSuccess]          = useState(null);
  const [isCapturing,      setIsCapturing]      = useState(false);
  const [cameraPermission, setCameraPermission] = useState(null);
  const [currentTime,      setCurrentTime]      = useState(new Date());

  useEffect(() => {
    const cssId = 'checkin-robot-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = CHECKIN_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!success && !error) return;
    const frame = window.requestAnimationFrame(() => {
      resultRef.current?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [success, error]);

  const checkCameraPermission = async () => {
    try {
      const result = await navigator.permissions.query({ name: 'camera' });
      setCameraPermission(result.state);
      result.addEventListener('change', () => setCameraPermission(result.state));
    } catch { setCameraPermission('unknown'); }
  };
  useEffect(() => { checkCameraPermission(); }, []);

  const openCamera = async () => {
    try {
      setError(null); setSuccess(null); setCameraOpen(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraPermission('granted');
      }
    } catch (err) {
      setCameraPermission('denied');
      if (err.name === 'NotAllowedError')    setError('Camera access denied. Please allow camera permissions and try again.');
      else if (err.name === 'NotFoundError') setError('No camera found. Please connect a camera and try again.');
      else setError('Unable to access camera. Please check your camera and try again.');
      setCameraOpen(false);
    }
  };

  const closeCamera = () => {
    setCameraOpen(false);
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const captureSnapshotBase64 = () => {
    if (!videoRef.current || !canvasRef.current) return null;
    const video = videoRef.current, canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  const handleFaceScan = async () => {
    let closeCameraAfter = true;
    let coords, imageBase64 = null;
    try {
      setIsCapturing(true); setError(null); setSuccess(null);
      try {
        coords = await getFaceAttendanceCoordinates();
      } catch (geoErr) {
        const code = geoErr.message;
        const geoMsg = code === 'GEO_DENIED'
          ? 'Location access is required. Allow location in your browser settings and try again.'
          : code === 'GEO_TIMEOUT'
            ? 'Could not get your location in time. Move outdoors or nearer a window, then try again.'
            : code === 'GEO_UNSUPPORTED'
              ? 'This browser does not support location. Try another browser.'
              : 'Could not determine your location. Check that location services are on and try again.';
        setError(geoMsg); closeCameraAfter = false; return;
      }
      imageBase64 = captureSnapshotBase64();
      if (!imageBase64) throw new Error('Failed to capture image');
      setLoading(true);
      const scanResponse = await axios.post('/public/face-attendance/check-in', {
        image: imageBase64, save_image: false,
        latitude: coords.latitude, longitude: coords.longitude,
        accuracy_m: coords.accuracy_m,
      });
      const { success, message, action, user, attendance, confidence } = scanResponse.data || {};
      const isCheckOut = action === 'check_out' || message === 'Checked out successfully';
      const isCheckIn  = action === 'check_in'  || message === 'Attendance recorded successfully';
      if (success && (isCheckOut || isCheckIn)) {
        setSuccess({ message: isCheckOut ? 'Check-out successful!' : 'Check-in successful!', user, attendance, confidence, action: isCheckOut ? 'check_out' : 'check_in' });
      } else { throw new Error(message || 'Face scan failed'); }
    } catch (err) {
      const backendMessage = err.response?.data?.message || '';
      let msg = 'Face scan failed. Please try again.';
      if (backendMessage.toLowerCase().includes('not recognized'))  msg = 'Face not recognized. Please center your face and try again.';
      else if (backendMessage.toLowerCase().includes('multiple'))   msg = 'Multiple faces detected. Ensure only one person is visible.';
      else if (backendMessage) msg = backendMessage;
      else if (err.message)   msg = err.message;
      setError(msg);
    } finally {
      setIsCapturing(false); setLoading(false);
      if (closeCameraAfter) closeCamera();
    }
  };

  if (loading && !cameraOpen) return <Loading message="Processing face recognition..." fullScreen={true} />;

  const timeStr = formatMalaysiaTime(currentTime) ?? currentTime.toLocaleTimeString('en-MY', { hour12: false });

  return (
    <Container maxWidth="md" sx={{ mt: 2, mb: 8 }}>

      {/* Header */}
      <Header
        title="Face Recognition Attendance"
        subtitle={`${formatMalaysiaNowString()} — Location must be enabled at the configured attendance site`}
        icon={FaceIcon}
        actionButton={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to="/"
              variant="outlined"
              startIcon={<PublicIcon />}
              sx={{
                color: TEXT_BODY, borderColor: alpha(TEXT_BODY, 0.22),
                bgcolor: alpha(SURFACE_UP, 0.62), textTransform: 'none',
                fontFamily: "'DM Sans', sans-serif", fontWeight: 600, borderRadius: '10px',
                '&:hover': { borderColor: alpha(NEON, 0.55), bgcolor: alpha(NEON, 0.08) },
              }}
            >
              Public website
            </Button>
            <Button
              component={RouterLink}
              to="/attendance"
              variant="contained"
              startIcon={<DashboardIcon />}
              sx={{
                color: '#fff', bgcolor: B700, textTransform: 'none',
                fontFamily: "'DM Sans', sans-serif", fontWeight: 700, borderRadius: '10px',
                boxShadow: `0 8px 24px ${alpha(B700, 0.25)}`,
                '&:hover': { bgcolor: B900 },
              }}
            >
              Staff portal
            </Button>
          </Box>
        }
      />

      {/* ── Recognition result ── */}
      <Box ref={resultRef} sx={{ scrollMarginTop: 16 }} />
      <Fade in={Boolean(error)} unmountOnExit>
        <Alert severity="error" onClose={() => setError(null)}
          sx={{ mb: 2.5, borderRadius: '12px', fontFamily: "'DM Sans', sans-serif",
            background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)',
            color: '#fca5a5', '& .MuiAlert-icon': { color: CORAL } }}>
          {error}
        </Alert>
      </Fade>

      <Fade in={Boolean(success)} unmountOnExit>
        <Alert severity="success" onClose={() => setSuccess(null)}
          sx={{ mb: 2.5, borderRadius: '12px', fontFamily: "'DM Sans', sans-serif",
            background: alpha('#021a0f', 0.95), border: `1px solid ${alpha(NEON3, 0.3)}`,
            color: alpha(NEON3, 0.9), '& .MuiAlert-icon': { color: NEON3 } }}>
          {success && (
            <Box>
              <Typography className="raj" sx={{ fontWeight: 700, fontSize: '1rem', mb: 1, color: NEON3 }}>
                {success.message}
              </Typography>
              {success.user && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1,
                  background: alpha(NEON3, 0.05), border: `1px solid ${alpha(NEON3, 0.15)}`,
                  borderRadius: '8px', p: 1 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '8px',
                    background: alpha(NEON3, 0.12), border: `1px solid ${alpha(NEON3, 0.3)}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PersonIcon sx={{ fontSize: 16, color: NEON3 }} />
                  </Box>
                  <Box>
                    <Typography className="dm" sx={{ fontWeight: 600, fontSize: '0.88rem', color: TEXT_BODY }}>
                      {success.user.name}
                    </Typography>
                    {success.user.matric_number && (
                      <Typography className="mono" sx={{ fontSize: '0.58rem', color: alpha(NEON3, 0.55), letterSpacing: 0.8 }}>
                        ID: {success.user.matric_number}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                {success.attendance?.check_in_time && (
                  <Chip icon={<AccessTimeIcon sx={{ fontSize: '11px !important', color: `${NEON3} !important` }} />}
                    label={`IN: ${formatMalaysiaTime(success.attendance.check_in_time) ?? success.attendance.check_in_time}`} size="small"
                    sx={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '0.58rem',
                      background: alpha(NEON3, 0.1), color: NEON3, border: `1px solid ${alpha(NEON3, 0.28)}`, borderRadius: '6px' }} />
                )}
                {success.action === 'check_out' && success.attendance?.check_out_time && (
                  <Chip icon={<AccessTimeIcon sx={{ fontSize: '11px !important', color: `${NEON} !important` }} />}
                    label={`OUT: ${formatMalaysiaTime(success.attendance.check_out_time) ?? success.attendance.check_out_time}`} size="small"
                    sx={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '0.58rem',
                      background: alpha(NEON, 0.1), color: NEON, border: `1px solid ${alpha(NEON, 0.28)}`, borderRadius: '6px' }} />
                )}
                {typeof success.confidence === 'number' && (
                  <Chip label={`${(success.confidence * 100).toFixed(1)}% match`} size="small"
                    sx={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '0.58rem',
                      background: alpha(NEON2, 0.1), color: NEON2, border: `1px solid ${alpha(NEON2, 0.28)}`, borderRadius: '6px' }} />
                )}
              </Box>
            </Box>
          )}
        </Alert>
      </Fade>

      {/* ── Main card ── */}
      <MotionBox
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.16)}`,
          background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          boxShadow: `0 0 0 1px ${alpha(B900, 0.9)}, 0 8px 32px ${alpha('#000', 0.5)}, inset 0 1px 0 ${alpha(NEON, 0.08)}`,
          overflow: 'hidden', position: 'relative',
          textAlign: 'center',
        }}
      >
        <CircuitBg />

        {/* Top sweep bar */}
        <Box sx={{
          height: '3px', position: 'relative', zIndex: 2,
          background: `linear-gradient(90deg, transparent, ${alpha(NEON, 0.9)} 30%, ${alpha(NEON2, 1)} 50%, ${alpha(NEON, 0.9)} 70%, transparent)`,
          backgroundSize: '200% 100%', animation: 'bar-sweep 5s linear infinite',
        }} />

        <Box sx={{ p: { xs: 3, sm: 4.5 }, position: 'relative', zIndex: 1 }}>

          {/* Scan orb */}
          <Box sx={{ position: 'relative', width: 160, height: 160, mx: 'auto', mb: 3 }}>
            <HudRing size={160} color={NEON}  speed="14s" dashes="20 8" />
            <HudRing size={140} color={NEON2} speed="9s" reverse dashes="8 16" />
            <HudRing size={120} color={alpha(NEON, 0.35)} speed="20s" dashes="4 4" />

            <Box
              component="button"
              onClick={cameraPermission !== 'denied' ? openCamera : undefined}
              sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                width: 100, height: 100, borderRadius: '50%',
                background: `radial-gradient(circle at 35% 35%, ${alpha(NEON, 0.22)}, ${alpha(B900, 0.8)} 60%, ${alpha(SURFACE_BASE, 0.9)})`,
                border: `2px solid ${alpha(NEON, cameraPermission === 'denied' ? 0.18 : 0.55)}`,
                boxShadow: cameraPermission === 'denied' ? 'none' : `0 0 0 5px ${alpha(NEON, 0.06)}, 0 0 26px ${alpha(NEON, 0.35)}, inset 0 0 16px ${alpha(NEON, 0.1)}`,
                cursor: cameraPermission === 'denied' ? 'not-allowed' : 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                transition: 'all 0.2s ease',
                animation: cameraPermission !== 'denied' ? 'face-pulse 2.8s ease-in-out infinite' : 'none',
                '&:hover': cameraPermission !== 'denied' ? {
                  boxShadow: `0 0 0 5px ${alpha(NEON, 0.1)}, 0 0 44px ${alpha(NEON, 0.55)}, inset 0 0 24px ${alpha(NEON, 0.15)}`,
                } : {},
              }}
            >
              <CameraIcon sx={{
                fontSize: 36,
                color: cameraPermission === 'denied' ? alpha(NEON, 0.2) : NEON,
                filter: cameraPermission !== 'denied' ? `drop-shadow(0 0 10px ${NEON})` : 'none',
                animation: cameraPermission !== 'denied' ? 'servo-tick 3s ease-in-out infinite' : 'none',
              }} />
              <Typography className="mono" sx={{ fontSize: '0.42rem', letterSpacing: 2.5, color: alpha(NEON, cameraPermission === 'denied' ? 0.2 : 0.65), textTransform: 'uppercase' }}>
                SCAN
              </Typography>
            </Box>

            {/* Corner brackets on orb zone */}
            {[{ top: 6, left: 6, corner: 'tl' }, { top: 6, right: 6, corner: 'tr' }, { bottom: 6, left: 6, corner: 'bl' }, { bottom: 6, right: 6, corner: 'br' }]
              .map((p, i) => <CornerBracket key={i} pos={p} size={14} thick={1.5} color={alpha(NEON, 0.45)} />)}
          </Box>

          {/* Title */}
          <Typography
            className="robot-title"
            data-text="FACE ATTENDANCE"
            sx={{ fontSize: { xs: '1.05rem', sm: '1.2rem' }, color: TEXT_BODY, letterSpacing: 3, mb: 0.8,
              textShadow: `0 0 20px ${alpha(NEON, 0.35)}` }}
          >
            FACE ATTENDANCE
          </Typography>
          <Typography className="dm" sx={{ fontSize: '0.82rem', color: TEXT_MUTED, mb: 3.5 }}>
            Scan once to check in · scan again to check out
          </Typography>

          {/* Camera denied warning */}
          {cameraPermission === 'denied' && (
            <Box sx={{ mb: 3, borderRadius: '10px', overflow: 'hidden',
              border: `1px solid ${alpha('#fbbf24', 0.22)}`, background: alpha('#1a0d00', 0.9) }}>
              <Box sx={{ height: '2px', background: `linear-gradient(90deg, transparent, #fbbf24, transparent)` }} />
              <Box sx={{ px: 2, py: 1.2 }}>
                <Typography className="dm" sx={{ fontSize: '0.82rem', color: '#fde68a' }}>
                  Camera access is blocked. Enable camera permissions in your browser settings to continue.
                </Typography>
              </Box>
            </Box>
          )}

          {/* CTA button */}
          <Button
            variant="contained" size="large"
            startIcon={<CameraIcon />}
            onClick={openCamera}
            disabled={cameraPermission === 'denied'}
            sx={{
              borderRadius: '12px', px: 5, py: 1.4,
              fontFamily: "'Rajdhani', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: 1,
              background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B700, 0.55)})`,
              border: `1.5px solid ${alpha(NEON, 0.45)}`, color: NEON,
              boxShadow: `0 0 20px ${alpha(NEON, 0.28)}, inset 0 1px 0 ${alpha(NEON, 0.1)}`,
              transition: 'all 0.22s ease',
              '&:hover': {
                background: `linear-gradient(135deg, ${alpha(NEON, 0.28)}, ${alpha(B700, 0.7)})`,
                color: '#fff', boxShadow: `0 0 34px ${alpha(NEON, 0.5)}`, transform: 'scale(1.03)',
              },
              '&.Mui-disabled': { background: alpha(SURFACE_UP, 0.7), color: alpha(NEON, 0.2), boxShadow: 'none', border: `1.5px solid ${alpha(NEON, 0.1)}` },
            }}
          >
            Scan Face
          </Button>

          {/* Status indicator strip */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3 }}>
            {[
              { dot: cameraPermission === 'granted' ? NEON3 : cameraPermission === 'denied' ? CORAL : alpha(NEON, 0.4), label: 'Camera' },
              { dot: NEON3, label: 'Geofence' },
              { dot: NEON, label: 'Encrypted' },
            ].map(({ dot, label }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: dot, boxShadow: `0 0 5px ${dot}`,
                  animation: 'status-beat 2s ease-in-out infinite' }} />
                <Typography className="mono" sx={{ fontSize: '0.52rem', color: alpha(TEXT_MUTED, 0.8), letterSpacing: 1 }}>{label}</Typography>
              </Box>
            ))}
          </Box>

        </Box>

        {/* Bottom bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: 1, borderTop: `1px solid ${alpha(NEON, 0.08)}`,
          background: alpha(SURFACE_BASE, 0.5), position: 'relative', zIndex: 1 }}>
          <Typography className="mono" sx={{ fontSize: '0.5rem', color: alpha(NEON, 0.28), letterSpacing: 1.5 }}>
            AES-256 // SECURE
          </Typography>
          <Typography className="mono" sx={{ fontSize: '0.5rem', color: alpha(NEON, 0.45), letterSpacing: 1.5 }}>
            {timeStr}
          </Typography>
        </Box>
      </MotionBox>

      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* ── Camera Dialog ── */}
      <Dialog
        open={cameraOpen} onClose={closeCamera}
        maxWidth="sm" fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '16px',
            background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.99)}, ${alpha(SURFACE_MID, 0.97)})`,
            border: `1px solid ${alpha(NEON, 0.2)}`,
            boxShadow: `0 32px 80px ${alpha('#000', 0.75)}, inset 0 1px 0 ${alpha(NEON, 0.08)}`,
            backdropFilter: 'blur(28px)',
            overflow: 'hidden', position: 'relative',
          },
        }}
        BackdropProps={{ sx: { background: 'rgba(2,6,15,0.85)', backdropFilter: 'blur(6px)' } }}
      >
        <CircuitBg />
        <Box sx={{
          height: '3px', position: 'relative', zIndex: 2,
          background: `linear-gradient(90deg, transparent, ${alpha(NEON, 0.9)} 30%, ${alpha(NEON2, 1)} 50%, ${alpha(NEON, 0.9)} 70%, transparent)`,
          backgroundSize: '200% 100%', animation: 'bar-sweep 5s linear infinite',
        }} />

        <DialogTitle sx={{ textAlign: 'center', pb: 1, position: 'relative', zIndex: 1,
          borderBottom: `1px solid ${alpha(NEON, 0.1)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.3 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: NEON3,
              animation: 'status-beat 1.6s ease-in-out infinite', boxShadow: `0 0 6px ${NEON3}` }} />
            <Typography className="mono" sx={{ fontSize: '0.52rem', color: alpha(NEON3, 0.55), letterSpacing: 2 }}>
              OPTICAL SENSOR ACTIVE
            </Typography>
          </Box>
          <Typography className="raj" sx={{ fontWeight: 700, fontSize: '1rem', color: TEXT_BODY, letterSpacing: 1.5 }}>
            BIOMETRIC CAPTURE
          </Typography>
          <Typography className="mono" sx={{ fontSize: '0.55rem', color: alpha(NEON, 0.4), letterSpacing: 1, mt: 0.2 }}>
            ALIGN FACE WITH TARGET RETICLE
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 0, position: 'relative', zIndex: 1 }}>
          <Box sx={{ position: 'relative', width: '100%', height: '370px',
            background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <video ref={videoRef} autoPlay playsInline muted
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

            {/* HUD overlay */}
            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {/* Face guide */}
              <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 200, height: 200,
                border: `2px solid ${alpha(NEON, 0.8)}`,
                borderRadius: '50%',
                animation: 'face-pulse 2s ease-in-out infinite',
              }}>
                <Box sx={{ position: 'absolute', inset: -8, borderRadius: '50%',
                  border: `1px dashed ${alpha(NEON, 0.3)}`,
                  animation: 'hud-rotate 10s linear infinite' }} />
                <Box sx={{ position: 'absolute', inset: -16, borderRadius: '50%',
                  border: `1px dashed ${alpha(NEON2, 0.2)}`,
                  animation: 'hud-rotate-rev 15s linear infinite' }} />
                <Box sx={{ position: 'absolute', top: '50%', left: '-30px', right: '-30px', height: '1px',
                  background: `linear-gradient(90deg, transparent 15%, ${alpha(NEON, 0.35)} 30%, ${alpha(NEON, 0.35)} 70%, transparent 85%)` }} />
                <Box sx={{ position: 'absolute', left: '50%', top: '-30px', bottom: '-30px', width: '1px',
                  background: `linear-gradient(180deg, transparent 15%, ${alpha(NEON, 0.35)} 30%, ${alpha(NEON, 0.35)} 70%, transparent 85%)` }} />
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                  width: 6, height: 6, borderRadius: '50%', background: NEON,
                  boxShadow: `0 0 8px ${NEON}`, animation: 'status-beat 1.5s ease-in-out infinite' }} />
              </Box>

              {/* Scan line */}
              <Box sx={{ position: 'absolute', left: 0, right: 0, height: '2px',
                background: `linear-gradient(90deg, transparent, ${alpha(NEON, 0.55)}, transparent)`,
                animation: 'scan-line 3s linear infinite' }} />

              {[{ top: 12, left: 12, corner: 'tl' }, { top: 12, right: 12, corner: 'tr' }, { bottom: 12, left: 12, corner: 'bl' }, { bottom: 12, right: 12, corner: 'br' }]
                .map((p, i) => <CornerBracket key={i} pos={p} size={26} thick={2} />)}

              {/* Status text */}
              <Box sx={{ position: 'absolute', bottom: 14, left: 0, right: 0, textAlign: 'center' }}>
                <Typography className="mono" sx={{ fontSize: '0.52rem', color: alpha(isCapturing ? NEON3 : NEON, 0.6), letterSpacing: 2 }}>
                  {isCapturing ? '● PROCESSING...' : '● AWAITING SUBJECT'}
                </Typography>
              </Box>

              {/* Time overlay */}
              <Box sx={{ position: 'absolute', top: 14, right: 50 }}>
                <Typography className="mono" sx={{ fontSize: '0.5rem', color: alpha(NEON, 0.38), letterSpacing: 1.5 }}>{timeStr}</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, justifyContent: 'center', gap: 1.5, position: 'relative', zIndex: 1,
          borderTop: `1px solid ${alpha(NEON, 0.1)}` }}>
          <Button onClick={closeCamera} disabled={isCapturing}
            sx={{
              borderRadius: '10px', px: 3, py: 0.9,
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.88rem',
              color: alpha(NEON, 0.65), background: alpha(SURFACE_UP, 0.5),
              border: `1.5px solid ${alpha(NEON, 0.2)}`,
              '&:hover': { background: alpha(NEON, 0.07), borderColor: alpha(NEON, 0.45), color: NEON },
              '&.Mui-disabled': { opacity: 0.35 },
            }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleFaceScan} disabled={isCapturing}
            startIcon={!isCapturing && <CameraIcon />}
            sx={{
              borderRadius: '10px', px: 4, py: 0.9,
              fontFamily: "'Rajdhani', sans-serif", fontSize: '0.95rem', fontWeight: 700, letterSpacing: 1,
              background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B700, 0.55)})`,
              border: `1.5px solid ${alpha(NEON, 0.45)}`, color: NEON,
              boxShadow: `0 0 18px ${alpha(NEON, 0.28)}, inset 0 1px 0 ${alpha(NEON, 0.1)}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                background: `linear-gradient(135deg, ${alpha(NEON, 0.28)}, ${alpha(B700, 0.7)})`,
                color: '#fff', boxShadow: `0 0 32px ${alpha(NEON, 0.5)}`, transform: 'scale(1.03)',
              },
              '&.Mui-disabled': { background: alpha(SURFACE_UP, 0.7), color: alpha(NEON, 0.2), boxShadow: 'none', border: `1.5px solid ${alpha(NEON, 0.08)}` },
            }}>
            {isCapturing
              ? <><CircularProgress size={14} sx={{ color: alpha(NEON, 0.7), mr: 1 }} />Processing…</>
              : 'Capture'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PublicAttendanceCheckIn;
